export const GTM_SCRIPT_ID = "thinkeat-gtm-script";
export const GOOGLE_TAG_SCRIPT_ID = "thinkeat-google-tag-script";

export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

export type DataLayerEntry = DataLayerEvent | unknown[];

interface DataLayerWindow extends Window {
  dataLayer?: DataLayerEntry[];
  gtag?: (...args: unknown[]) => void;
  __thinkeatAnalyticsDisabled?: boolean;
  [key: `ga-disable-${string}`]: boolean | undefined;
}

interface PageViewLocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

const SAFE_ANALYTICS_QUERY_PARAM_PATTERNS = [
  /^utm_/i,
  /^(gclid|dclid|gbraid|wbraid|fbclid|msclkid)$/i,
];

let previousTrackedPageLocation: string | null = null;

function getWindowWithDataLayer(): DataLayerWindow {
  // `DataLayerWindow` adds a `ga-disable-*` index signature that lib.dom's `Window` cannot
  // structurally satisfy, so the direct assertion is rejected; the properties are all added at
  // runtime by GTM / `setAnalyticsDisabled`, which is exactly what this accessor documents.
  return window as unknown as DataLayerWindow;
}

export function ensureDataLayer(): DataLayerEvent[] {
  const targetWindow = getWindowWithDataLayer();
  targetWindow.dataLayer ??= [];
  return targetWindow.dataLayer as DataLayerEvent[];
}

export function pushDataLayerEvent(event: DataLayerEvent): void {
  if (typeof window === "undefined") return;

  if (getWindowWithDataLayer().__thinkeatAnalyticsDisabled) {
    return;
  }

  ensureDataLayer().push(event);

  if (import.meta.env.DEV) {
    console.warn("[analytics:dataLayer]", event);
  }
}

function ensureGoogleTagFunction(): (...args: unknown[]) => void {
  const targetWindow = getWindowWithDataLayer();
  targetWindow.dataLayer ??= [];

  if (!targetWindow.gtag) {
    targetWindow.gtag = (...args: unknown[]) => {
      targetWindow.dataLayer?.push(args);
    };
  }

  return targetWindow.gtag;
}

function setGoogleAnalyticsDisabled(disabled: boolean): void {
  const { gaMeasurementId } = getAnalyticsConfiguration();

  if (!gaMeasurementId || typeof window === "undefined") {
    return;
  }

  getWindowWithDataLayer()[`ga-disable-${gaMeasurementId}`] = disabled;
}

export function getAnalyticsConfiguration(): {
  gtmContainerId: string | null;
  gaMeasurementId: string | null;
} {
  const normalizedGtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID?.trim() || "";
  const normalizedGaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || "";

  return {
    gtmContainerId: normalizedGtmContainerId || null,
    gaMeasurementId: normalizedGaMeasurementId || null,
  };
}

function isSafeAnalyticsQueryParameter(parameterName: string): boolean {
  return SAFE_ANALYTICS_QUERY_PARAM_PATTERNS.some((pattern) => pattern.test(parameterName));
}

function sanitizeAnalyticsSearch(search: string): string {
  if (!search) {
    return "";
  }

  const searchParams = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const sanitizedSearchParams = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (isSafeAnalyticsQueryParameter(key)) {
      sanitizedSearchParams.append(key, value);
    }
  }

  const sanitizedQuery = sanitizedSearchParams.toString();
  return sanitizedQuery ? `?${sanitizedQuery}` : "";
}

function getCurrentDocumentReferrer(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  return document.referrer || null;
}

export function resetAnalyticsTrackingState(): void {
  previousTrackedPageLocation = null;
}

/** Remove GTM / gtag script tags this app injected (consent revoke). Third-party beacons may still flush briefly. */
export function removeAnalyticsInjectedScripts(): void {
  if (typeof document === "undefined") return;
  document.getElementById(GTM_SCRIPT_ID)?.remove();
  document.getElementById(GOOGLE_TAG_SCRIPT_ID)?.remove();
}

export function setAnalyticsRuntimeDisabled(disabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }

  const targetWindow = getWindowWithDataLayer();
  targetWindow.__thinkeatAnalyticsDisabled = disabled;
  setGoogleAnalyticsDisabled(disabled);
}

export function loadGtmContainer(containerId: string): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const normalizedContainerId = containerId.trim();
  if (!normalizedContainerId) {
    return false;
  }

  setAnalyticsRuntimeDisabled(false);

  if (document.getElementById(GTM_SCRIPT_ID)) {
    return false;
  }

  pushDataLayerEvent({
    event: "gtm.js",
    "gtm.start": Date.now(),
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(normalizedContainerId)}`;
  document.head.appendChild(script);

  return true;
}

export function loadGoogleTag(measurementId: string): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const normalizedMeasurementId = measurementId.trim();
  if (!normalizedMeasurementId) {
    return false;
  }

  setAnalyticsRuntimeDisabled(false);

  const gtag = ensureGoogleTagFunction();

  if (!document.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedMeasurementId)}`;
    document.head.appendChild(script);
  }

  const alreadyConfigured = getWindowWithDataLayer().dataLayer?.some(
    (entry) =>
      Array.isArray(entry) &&
      entry[0] === "config" &&
      entry[1] === normalizedMeasurementId,
  );

  if (alreadyConfigured) {
    return false;
  }

  gtag("js", new Date());
  gtag("config", normalizedMeasurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  return true;
}

export function trackAnalyticsEvent(
  eventName: string,
  params?: Record<string, string | number>,
): void {
  const { gtmContainerId, gaMeasurementId } = getAnalyticsConfiguration();

  if (gtmContainerId) {
    pushDataLayerEvent({ event: eventName, ...params });
    return;
  }

  if (!gaMeasurementId || typeof window === "undefined") {
    return;
  }

  if (getWindowWithDataLayer().__thinkeatAnalyticsDisabled) {
    return;
  }

  const gtag = ensureGoogleTagFunction();
  // Omit the third argument entirely when there are no params, rather than
  // handing gtag an explicit `undefined`.
  if (params) {
    gtag("event", eventName, params);
    return;
  }
  gtag("event", eventName);
}

/**
 * Canonical page URL for HashRouter (`origin/#/path?query`).
 * Intentionally ignores `location.hash` sub-fragments: they are not part of the routed path,
 * can duplicate `#` in `page_location`, and may carry sensitive or noisy client-only state.
 */
export function buildHashRouterPageLocation({ pathname, search = "" }: PageViewLocationLike): string {
  const normalizedPath = pathname || "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/#${normalizedPath}${search}`;
}

function buildAnalyticsFallbackTitle(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "ThinkEat";
  }

  return pathname
    .split("/")
    .filter(Boolean)
    .map((segment) =>
      segment
        .split("-")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    )
    .join(" · ");
}

function resolvePageTitle(pageTitle: string, pathname: string): string {
  const normalizedTitle = pageTitle.trim();

  if (
    normalizedTitle &&
    normalizedTitle !== "ThinkEat" &&
    normalizedTitle !== "ThinkEat - Planificación de Menús Personalizada"
  ) {
    return normalizedTitle;
  }

  return buildAnalyticsFallbackTitle(pathname);
}

export function buildPageViewEvent(location: PageViewLocationLike, pageTitle: string): DataLayerEvent {
  const pathname = location.pathname || "/";
  const search = sanitizeAnalyticsSearch(location.search ?? "");

  return {
    event: "thinkeat_page_view",
    page_location: buildHashRouterPageLocation({ pathname, search }),
    page_path: `${pathname}${search}`,
    page_title: resolvePageTitle(pageTitle, pathname),
  };
}

/** Push a page view built with {@link buildPageViewEvent} (avoids rebuilding / diverging from dedupe logic). */
export function trackPageViewEvent(pageViewEvent: DataLayerEvent): DataLayerEvent {
  const { gtmContainerId, gaMeasurementId } = getAnalyticsConfiguration();
  const event: DataLayerEvent = { ...pageViewEvent };
  const pageReferrer = previousTrackedPageLocation ?? getCurrentDocumentReferrer();

  if (pageReferrer) {
    event.page_referrer = pageReferrer;
  }

  previousTrackedPageLocation =
    typeof event.page_location === "string" ? event.page_location : null;

  if (gtmContainerId) {
    pushDataLayerEvent(event);
    return event;
  }

  if (gaMeasurementId && typeof window !== "undefined") {
    const gtag = ensureGoogleTagFunction();
    gtag("event", "page_view", {
      page_location: event.page_location,
      page_path: event.page_path,
      page_referrer: event.page_referrer,
      page_title: event.page_title,
    });
  }

  return event;
}
